
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { EcommerceSettings } from '@/components/admin/campaigns/types';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { InfoIcon } from 'lucide-react';

interface EcommerceTrackingTabProps {
  settings: EcommerceSettings;
  updateSettings: (settings: Partial<EcommerceSettings>) => void;
}

const EcommerceTrackingTab: React.FC<EcommerceTrackingTabProps> = ({
  settings,
  updateSettings,
}) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Tracking Configuration</CardTitle>
        <CardDescription>
          Set up how referrals are tracked through your e-commerce store
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="trackingParameter">Tracking Parameter</Label>
          <Input
            id="trackingParameter"
            value={settings.trackingParameter}
            onChange={(e) => updateSettings({ trackingParameter: e.target.value })}
            placeholder="ref"
          />
          <p className="text-sm text-muted-foreground">
            This parameter will be added to URLs to track referrals (e.g., yourstore.com?{settings.trackingParameter}=CODE)
          </p>
        </div>

        <Alert className="bg-blue-50 border-blue-200">
          <InfoIcon className="h-4 w-4 text-blue-600" />
          <AlertTitle>Tracking Information</AlertTitle>
          <AlertDescription className="text-sm">
            <p className="mb-2">
              Our system will automatically track referrals through:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>URL parameters in your store links</li>
              <li>Browser cookies (30-day attribution window)</li>
              <li>Checkout process integration</li>
            </ul>
          </AlertDescription>
        </Alert>

        <Alert className="bg-muted">
          <InfoIcon className="h-4 w-4" />
          <AlertTitle>Implementation Tip</AlertTitle>
          <AlertDescription>
            After saving your integration settings, you'll need to add our tracking script to your store.
            Installation instructions will be provided based on your selected platform.
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  );
};

export default EcommerceTrackingTab;
